import { createFileRoute, Link, Outlet, redirect } from "@tanstack/react-router";
import { Loader2, Shield, ListChecks, History, Home, Inbox, Activity, Megaphone, TrendingUp, Eye, GitBranch, Microscope, Search, Cpu, Film, Mail, Users, ChevronDown, NotebookPen, BookOpen, Archive, Bell } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { fetchPendingDraftCount } from "@/lib/signal-drafts";
import { UserMenuDropdown } from "@/components/dashboard/UserMenuDropdown";
import { useGoldPrice } from "@/hooks/useGoldPrice";
import { useEffect, useState } from "react";
import { useLocation } from "@tanstack/react-router";

const ADMIN_PAGE_LABELS: { prefix: string; title: string; subtitle: string }[] = [
  { prefix: "/admin/push-tests", title: "Notification Tests", subtitle: "Private Device Testing" },
  { prefix: "/admin/signals/drafts", title: "Drafts", subtitle: "Pending Review" },
  { prefix: "/admin/signals", title: "Signals", subtitle: "Signal Operations" },
  { prefix: "/admin/history", title: "History", subtitle: "Trade Archive" },
  { prefix: "/admin/broadcasts", title: "Broadcasts", subtitle: "Member Messaging" },
  { prefix: "/admin/waitlist", title: "Waitlist", subtitle: "Subscription Requests" },
  { prefix: "/admin/weekly-outlook", title: "Weekly Outlook", subtitle: "Report Generation" },
  { prefix: "/admin/otas-shadow", title: "SMA Shadow", subtitle: "Research & Lab" },
  { prefix: "/admin/oeas-shadow", title: "OEAS Lab", subtitle: "Research & Lab" },
  { prefix: "/admin/otas", title: "OTAS-01 Tactical", subtitle: "Live Engine" },
  { prefix: "/admin/swing-shadow", title: "OSAS-01 Strategic", subtitle: "Live Swing Engine" },
  { prefix: "/admin/wip/command-center", title: "Command Center", subtitle: "WIP Reference" },
  { prefix: "/admin/wip/trading-room", title: "Trading Room", subtitle: "WIP Reference" },
  { prefix: "/admin/subscribers", title: "Subscribers", subtitle: "Member Directory" },
  { prefix: "/admin/acquisition", title: "Acquisition", subtitle: "Growth Funnel" },
  { prefix: "/admin", title: "Admin Overview", subtitle: "Operations Desk" },
];

function resolveAdminLabel(pathname: string) {
  const match = ADMIN_PAGE_LABELS.find(
    (entry) => pathname === entry.prefix || pathname.startsWith(entry.prefix + "/"),
  );
  return match ?? ADMIN_PAGE_LABELS[ADMIN_PAGE_LABELS.length - 1];
}

export const Route = createFileRoute("/_authenticated/admin")({
  beforeLoad: async () => {
    const { data: sess } = await supabase.auth.getSession();
    const userId = sess.session?.user.id;
    if (!userId) {
      throw redirect({ to: "/auth", search: { tab: "login" } });
    }
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle();
    if (error || !data) {
      throw redirect({ to: "/dashboard" });
    }
  },
  component: AdminLayout,
});

function AdminLayout() {
  const { isAdmin, isLoading } = useIsAdmin();
  const location = useLocation();
  const { title, subtitle } = resolveAdminLabel(location.pathname);
  const [scrolled, setScrolled] = useState(false);
  const { price, direction } = useGoldPrice(60_000);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setScrolled((prev) => (prev ? window.scrollY > 180 : window.scrollY > 240));
        ticking = false;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="max-w-md rounded-xl border border-border/60 bg-card p-8 text-center">
          <Shield className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
          <h1 className="font-display text-xl font-bold">Access denied</h1>
          <p className="mt-2 text-sm text-muted-foreground">You need admin privileges to view this area.</p>
          <Link to="/dashboard" className="mt-4 inline-block text-sm text-gold underline">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full max-w-full overflow-x-clip bg-background">
        <AdminSidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          <header
            className={`sticky top-0 z-40 flex items-center gap-3 px-4 backdrop-blur-xl transition-[height,background-color] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
              scrolled ? "h-12 bg-background/50" : "h-16 bg-background/30"
            }`}
            style={{
              boxShadow: "inset 0 1px 0 0 rgba(255,255,255,0.06)",
            }}
          >
            <SidebarTrigger />
            <div className="flex min-w-0 flex-col leading-tight">
              <div className="grid">
                <div
                  className={`col-start-1 row-start-1 truncate font-medium transition-[opacity,transform,font-size] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                    scrolled && price != null
                      ? "pointer-events-none -translate-y-1 text-xs opacity-0"
                      : scrolled
                        ? "text-xs"
                        : "text-sm"
                  }`}
                  aria-hidden={scrolled && price != null}
                >
                  {title}
                </div>
                <div
                  className={`col-start-1 row-start-1 flex items-baseline gap-2 transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
                    scrolled && price != null ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-1 opacity-0"
                  }`}
                  aria-hidden={!(scrolled && price != null)}
                >
                  <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
                    XAU/USD
                  </span>
                  <span className="font-mono text-sm font-semibold tabular-nums text-foreground">
                    {price != null ? price.toFixed(2) : ""}
                  </span>
                  {(direction === "up" || direction === "down") && (
                    <span
                      className={`font-mono text-[10px] ${
                        direction === "up" ? "text-emerald" : "text-destructive"
                      }`}
                    >
                      {direction === "up" ? "▲" : "▼"}
                    </span>
                  )}
                </div>
              </div>
              <div
                className="grid transition-[grid-template-rows,opacity] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]"
                style={{ gridTemplateRows: scrolled ? "0fr" : "1fr", opacity: scrolled ? 0 : 1 }}
              >
                <div className="min-h-0 overflow-hidden font-mono text-[9px] uppercase tracking-[0.18em] text-muted-foreground">
                  {subtitle}
                </div>
              </div>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Link to="/dashboard" className="rounded-md px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground">
                Exit to dashboard
              </Link>
              <UserMenuDropdown compact={scrolled} />
            </div>
          </header>
          <main className="container mx-auto max-w-7xl flex-1 px-4 py-8">
            <Outlet />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}

type AdminNavItem = { title: string; url: string; icon: typeof Home; exact?: boolean; drafts?: boolean };

const ADMIN_GROUPS: { label: string; items: AdminNavItem[] }[] = [
  {
    label: "Operations",
    items: [
      { title: "Overview", url: "/admin", icon: Home, exact: true },
      { title: "Signals", url: "/admin/signals", icon: ListChecks, exact: true },
      { title: "Drafts", url: "/admin/signals/drafts", icon: Inbox, drafts: true },
      { title: "History", url: "/admin/history", icon: History },
      { title: "Case Studies", url: "/case-studies", icon: Film },
      { title: "Broadcasts", url: "/admin/broadcasts", icon: Megaphone },
      { title: "Notification Tests", url: "/admin/push-tests", icon: Bell },
      { title: "Waitlist", url: "/admin/waitlist", icon: Mail },
      { title: "Weekly Outlook", url: "/admin/weekly-outlook", icon: Film },
    ],
  },
  {
    label: "Engines",
    items: [
      { title: "OTAS-01 Tactical", url: "/admin/otas", icon: Cpu },
      { title: "OSAS-01 Strategic", url: "/admin/swing-shadow", icon: TrendingUp },
    ],
  },
  {
    label: "Research & Lab",
    items: [
      { title: "SMA Shadow", url: "/admin/otas-shadow", icon: GitBranch },
      { title: "OEAS Lab", url: "/admin/oeas-shadow", icon: Microscope },
    ],
  },
  {
    label: "WIP",
    items: [
      { title: "Command Center", url: "/admin/wip/command-center", icon: Archive },
      { title: "Trading Room", url: "/admin/wip/trading-room", icon: Archive },
    ],
  },
  {
    label: "Platform",
    items: [
      { title: "Subscribers", url: "/admin/subscribers", icon: Users },
      { title: "Acquisition", url: "/admin/acquisition", icon: TrendingUp },
    ],
  },
];

function AdminSidebar() {
  const { isMobile, setOpenMobile } = useSidebar();
  const { data: draftCount = 0 } = useQuery({
    queryKey: ["signal-drafts-count"],
    queryFn: fetchPendingDraftCount,
    refetchInterval: 30_000,
  });
  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <div className="flex items-center gap-2 px-2 py-1.5 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
          <Shield className="h-5 w-5 shrink-0 text-gold" />
          <span className="font-display text-base font-bold group-data-[collapsible=icon]:hidden">Admin Desk</span>
        </div>
      </SidebarHeader>
      <SidebarContent>
        {ADMIN_GROUPS.map((g) => (
          <SidebarGroup key={g.label}>
            <SidebarGroupLabel className="font-mono text-[10px] uppercase tracking-wider">{g.label}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {g.items.map((item) => (
                  <SidebarMenuItem key={item.url}>
                    <SidebarMenuButton asChild tooltip={item.title}>
                      <Link
                        to={item.url}
                        activeOptions={{ exact: item.exact }}
                        activeProps={{ className: "bg-gold-muted text-gold" }}
                        onClick={() => isMobile && setOpenMobile(false)}
                        className="relative"
                      >
                        <item.icon className="h-4 w-4 shrink-0" />
                        <span className="truncate group-data-[collapsible=icon]:hidden">{item.title}</span>
                        {item.drafts && draftCount > 0 && (
                          <>
                            <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-gold px-1.5 text-[10px] font-bold text-gold-foreground group-data-[collapsible=icon]:hidden">
                              {draftCount}
                            </span>
                            <span className="absolute right-1 top-1 hidden h-1.5 w-1.5 rounded-full bg-gold group-data-[collapsible=icon]:block" aria-hidden="true" />
                          </>
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
        <Collapsible className="group/wip group-data-[collapsible=icon]:hidden">
          <SidebarGroup>
            <CollapsibleTrigger className="flex w-full items-center justify-between px-2 py-1.5 font-mono text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground">
              Work in progress
              <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]/wip:rotate-180" />
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarGroupContent>
                <SidebarMenu>
                  {[
                    { title: "Journal (WIP)", url: "/dashboard/journal", icon: NotebookPen },
                    { title: "Library (WIP)", url: "/dashboard/ebooks", icon: BookOpen },
                  ].map((item) => (
                    <SidebarMenuItem key={item.url}>
                      <SidebarMenuButton asChild tooltip={item.title}>
                        <Link
                          to={item.url}
                          activeProps={{ className: "bg-gold-muted text-gold" }}
                          onClick={() => isMobile && setOpenMobile(false)}
                        >
                          <item.icon className="h-4 w-4" />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </CollapsibleContent>
          </SidebarGroup>
        </Collapsible>
      </SidebarContent>
    </Sidebar>
  );
}
